import { verificationsApi } from '../../../data/verificationsApi';
import type { PayerGroupSummaryRow, WorklistItem, WorklistType } from '../../../data/views';
import type { WorklistTypeView } from './worklistViews';

/** Raw backend payer-grouped row (`WorklistPayerGroupedView`, camelCased). */
interface WorklistPayerGroupedView {
  payer: string;
  count: number;
}

/**
 * Worklist feature endpoints, injected into the single verifications base api.
 *
 * Owns the worklist reads: the typed worklist grid, the payer-grouped summary, and the
 * worklist-category dimension. Endpoints are feature-owned (not shared); other slices that need a
 * worklist read inject their own under a distinct name.
 */
export const worklistEndpoints = verificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    getWorklistByType: builder.query<
      WorklistItem[],
      { type: WorklistType; center?: string }
    >({
      query: ({ type, center }) => ({
        url: '/verification/worklist',
        method: 'GET',
        params: { type, center },
      }),
      providesTags: (result, error, { type, center }) => [
        { type: 'Worklist', id: `${type}|${center ?? 'ALL'}` },
        'Worklist',
      ],
    }),

    getWorklistPayerGrouped: builder.query<
      PayerGroupSummaryRow[],
      { type: WorklistType; center?: string }
    >({
      query: ({ type, center }) => ({
        url: '/verification/worklist/payer-grouped',
        method: 'GET',
        params: { type, center },
      }),
      // Surface the backend `{ payer, count }` rows as `{ payerGroup, count }` — the payer
      // value carries the calculator type (user-facing "payer group").
      transformResponse: (rows: WorklistPayerGroupedView[]): PayerGroupSummaryRow[] =>
        (rows ?? []).map((row) => ({ payerGroup: row.payer, count: row.count })),
      providesTags: (result, error, { type, center }) => [
        { type: 'Worklist', id: `payer-grouped|${type}|${center ?? 'ALL'}` },
        'Worklist',
      ],
    }),

    getWorklistTypes: builder.query<WorklistTypeView[], void>({
      query: () => ({
        url: '/verification/dimensions/worklist-types',
        method: 'GET',
      }),
      providesTags: ['WorklistTypes'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWorklistByTypeQuery,
  useGetWorklistPayerGroupedQuery,
  useGetWorklistTypesQuery,
} = worklistEndpoints;
